import { useMemo } from 'react';
import { RfUiDatepicker, RfUiInput } from 'renfe-components';
import { makeSelectPracticaSeleccionada } from 'store/practicas/selectors';
import { language } from 'utils/literals';
import { timestampToTimeString } from 'utils/dateTimeUtils';
import { createStructuredSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import { stateLiteral } from 'utils/statesLiterals';
import {
  makeSelectIsStudentProfile,
  makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected,
} from 'store/usuarios/selectors';
import {
  makeSelectFinishTimeStampSelectedJourney,
  makeSelectFinishDateTimeSelectedService,
  makeSelectFinishTimeSelectedService,
  makeSelectStartTimeStampSelectedJourney,
  makeSelectStartDateTimeSelectedService,
  makeSelectStartTimeSelectedService,
  makeSelectStartDateTimeStampSelectedJourney,
  makeSelectStartServiceSelectedErrorDraft,
  makeSelectFinishTimeServiceSelectedErrorDraft,
  makeSelectStartTimeSelectedServicePristine,
  makeSelectFinishTimeSelectedServicePristine,
} from '../../store/app/selectors';
import {
  setStartDateTimeSelectedService,
  setFinishDateTimeSelectedService,
  setStartTimeSelectedServicePristine,
  setFinishTimeSelectedServicePristine,
} from '../../store/app/actions';
import { MinutesContainer } from 'containers/minutesContainer';
import { PracticaSeleccionada } from 'store/practicas/types';

const selectors = createStructuredSelector({
  practicaSeleccionada: makeSelectPracticaSeleccionada(),
  timeStampJourneySelectedStart: makeSelectStartTimeStampSelectedJourney(),
  dateJourneySelectedStart: makeSelectStartDateTimeStampSelectedJourney(),
  timeStampJourneySelectedFinish: makeSelectFinishTimeStampSelectedJourney(),
  timeSelectedServiceStart: makeSelectStartTimeSelectedService(),
  timeSelectedServiceFinish: makeSelectFinishTimeSelectedService(),
  profileActive: makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
  startDateTimeSelectedService: makeSelectStartDateTimeSelectedService(),
  startTimeSelectedServicePristine:
    makeSelectStartTimeSelectedServicePristine(),
  finishDateTimeSelectedService: makeSelectFinishDateTimeSelectedService(),
  finishTimeSelectedServicePristine:
    makeSelectFinishTimeSelectedServicePristine(),
  startServiceSelectedErrorDraft: makeSelectStartServiceSelectedErrorDraft(),
  finishTimeServiceSelectedErrorDraft:
    makeSelectFinishTimeServiceSelectedErrorDraft(),
  isStudentProfile: makeSelectIsStudentProfile(),
} as any);

const nowTimeStamp: number = new Date().getTime();

const ServicesContainer = () => {
  const dispatch = useDispatch();
  const {
    practicaSeleccionada,
    timeStampJourneySelectedStart,
    timeStampJourneySelectedFinish,
    startDateTimeSelectedService,
    startTimeSelectedServicePristine,
    finishDateTimeSelectedService,
    finishTimeSelectedServicePristine,
    startServiceSelectedErrorDraft,
    finishTimeServiceSelectedErrorDraft,
    isStudentProfile,
  } = useSelector<any, any>(selectors);

  const editMode: boolean = useMemo(
    () => !!practicaSeleccionada.CUERPO_FICHA,
    [practicaSeleccionada]
  );

  const calculatedStartDateSelectedService: string | undefined = useMemo(() => {
    if (startDateTimeSelectedService) {
      const date = new Date(startDateTimeSelectedService);
      date.setHours(0, 0, 0, 0);
      return date.getTime().toString();
    }
  }, [startDateTimeSelectedService]);

  const calculatedStartTimeSelectedService: string = useMemo(() => {
    if (startTimeSelectedServicePristine) {
      return '--:--';
    }
    return timestampToTimeString(startDateTimeSelectedService);
  }, [startDateTimeSelectedService, startTimeSelectedServicePristine]);

  const calculatedFinishDateSelectedService: string = useMemo(() => {
    return finishDateTimeSelectedService
      ? finishDateTimeSelectedService.getTime().toString()
      : null;
  }, [finishDateTimeSelectedService]);

  const calculatedFinishTimeSelectedService: string = useMemo(() => {
    if (finishTimeSelectedServicePristine) {
      return '--:--';
    }
    return timestampToTimeString(finishDateTimeSelectedService);
  }, [finishDateTimeSelectedService, finishTimeSelectedServicePristine]);

  // Condiciones de edición

  const canEditStartDateSelectedService: boolean = useMemo(() => {
    const estadoPractica =
      (practicaSeleccionada as PracticaSeleccionada)?.CUERPO_FICHA
        ?.ID_ESTADO_PRACTICA ?? null;
    return (
      isStudentProfile &&
      !!estadoPractica &&
      [stateLiteral.borrador, stateLiteral.pendCorrecion].indexOf(
        estadoPractica
      ) > -1
    );
  }, [isStudentProfile, practicaSeleccionada]);

  const canEditStartTimeSelectedService: boolean = useMemo(() => {
    const estadoPractica =
      (practicaSeleccionada as PracticaSeleccionada)?.CUERPO_FICHA
        ?.ID_ESTADO_PRACTICA ?? null;
    return (
      isStudentProfile &&
      !!estadoPractica &&
      [stateLiteral.borrador, stateLiteral.pendCorrecion].indexOf(
        estadoPractica
      ) > -1
    );
  }, [isStudentProfile, practicaSeleccionada]);

  const canEditFinishDateSelectedService: boolean = useMemo(() => {
    const estadoPractica =
      (practicaSeleccionada as PracticaSeleccionada)?.CUERPO_FICHA
        ?.ID_ESTADO_PRACTICA ?? null;
    return (
      isStudentProfile &&
      !!estadoPractica &&
      [stateLiteral.borrador, stateLiteral.pendCorrecion].indexOf(
        estadoPractica
      ) > -1
    );
  }, [isStudentProfile, practicaSeleccionada]);

  const canEditFinishTimeSelectedService: boolean = useMemo(() => {
    const estadoPractica =
      (practicaSeleccionada as PracticaSeleccionada)?.CUERPO_FICHA
        ?.ID_ESTADO_PRACTICA ?? null;
    return (
      isStudentProfile &&
      !!estadoPractica &&
      [stateLiteral.borrador, stateLiteral.pendCorrecion].indexOf(
        estadoPractica
      ) > -1
    );
  }, [isStudentProfile, practicaSeleccionada]);

  // Eventos

  const handleChangeStartDateSelectedService = (e: CustomEvent) => {
    const newDate = startDateTimeSelectedService
      ? new Date(startDateTimeSelectedService)
      : new Date();
    const inputDate = new Date(e.detail);
    newDate.setFullYear(
      inputDate.getFullYear(),
      inputDate.getMonth(),
      inputDate.getDate()
    );
    dispatch(setStartDateTimeSelectedService(newDate));
  };

  const handleChangeStartTimeSelectedService = (e: CustomEvent) => {
    const newDate = startDateTimeSelectedService
      ? new Date(startDateTimeSelectedService)
      : new Date();
    const data = e.detail ? e.detail.split(':') : null;
    if (data?.length === 2) {
      dispatch(setStartTimeSelectedServicePristine(false));
      const [hoursStr, minutesStr] = data;
      newDate.setHours(parseInt(hoursStr), parseInt(minutesStr), 59, 999);
    } else {
      dispatch(setStartTimeSelectedServicePristine(true));
      newDate.setHours(0, 0, 0, 0);
    }
    dispatch(setStartDateTimeSelectedService(newDate));
  };

  const handleChangeFinishDateSelectedService = (e: CustomEvent) => {
    const newDate = finishDateTimeSelectedService
      ? new Date(finishDateTimeSelectedService)
      : new Date();
    const inputDate = new Date(e.detail);
    newDate.setFullYear(
      inputDate.getFullYear(),
      inputDate.getMonth(),
      inputDate.getDate()
    );
    dispatch(setFinishDateTimeSelectedService(newDate));
  };

  const handleChangeFinishTimeSelectedService = (e: CustomEvent) => {
    const newDate = finishDateTimeSelectedService
      ? new Date(finishDateTimeSelectedService)
      : new Date();
    const data = e.detail ? e.detail.split(':') : null;
    if (data?.length === 2) {
      dispatch(setFinishTimeSelectedServicePristine(false));
      const [hoursStr, minutesStr] = data;
      newDate.setHours(parseInt(hoursStr), parseInt(minutesStr), 59, 999);
    } else {
      dispatch(setFinishTimeSelectedServicePristine(true));
      newDate.setHours(0, 0, 0, 0);
    }
    dispatch(setFinishDateTimeSelectedService(newDate));
  };

  return (
    <>
      {editMode ? (
        // Detail Practice
        <div>
          <h2 className="rf-title rf-title--secundary">
            {language.informacionPractica.servicioLabel}
          </h2>
          <div>
            <div className="rf-box__child">
              <div className="rf-practice-detail__input-container rf-practice-list__input-container--pr">
                <RfUiDatepicker
                  required={'black'}
                  moment-locale="es"
                  position={'left'}
                  defaultMaxDateFrom={nowTimeStamp.toString()}
                  labelInput={language.informacionPractica.dateDesdeLabel}
                  disabled={!canEditStartDateSelectedService}
                  text-apply-button={language.informacionPractica.aplicarLabel}
                  onDateSelected={handleChangeStartDateSelectedService}
                  defaultDateFrom={calculatedStartDateSelectedService}
                />
              </div>
            </div>
            <div className="rf-box__child">
              <div className="rf-practice-detail__input-container">
                <RfUiDatepicker
                  required={'black'}
                  moment-locale="es"
                  position={'left'}
                  defaultMaxDateFrom={nowTimeStamp.toString()}
                  disabled={!canEditFinishDateSelectedService}
                  labelInput={language.informacionPractica.dateHastaLabel}
                  text-apply-button={language.informacionPractica.aplicarLabel}
                  onDateSelected={handleChangeFinishDateSelectedService}
                  defaultDateFrom={calculatedFinishDateSelectedService}
                />
              </div>
            </div>
            <div className="rf-box__child">
              <div className="rf-practice-detail__input-container rf-practice-list__input-container--pr">
                <RfUiInput
                  required={'black'}
                  label={language.informacionPractica.hourInicioLabel}
                  textError={startServiceSelectedErrorDraft}
                  disabled={!canEditStartTimeSelectedService}
                  error=""
                  typeInput="time"
                  className="rf-input"
                  onValueChanges={handleChangeStartTimeSelectedService}
                  value={calculatedStartTimeSelectedService}
                />
              </div>
            </div>
            <div className="rf-box__child">
              <div className="rf-practice-detail__input-container">
                <RfUiInput
                  required={'black'}
                  label={language.informacionPractica.hourFinLabel}
                  textError={finishTimeServiceSelectedErrorDraft}
                  disabled={!canEditFinishTimeSelectedService}
                  error=""
                  typeInput="time"
                  className="rf-input"
                  onValueChanges={handleChangeFinishTimeSelectedService}
                  value={calculatedFinishTimeSelectedService}
                />
              </div>
            </div>
            <MinutesContainer />
          </div>
        </div>
      ) : (
        // Create Practice
        <div>
          <h2 className="rf-title rf-title--secundary">
            {language.informacionPractica.servicioLabel}
          </h2>
          <div>
            <div className="rf-box__child">
              <div className="rf-practice-create__input-container rf-practice-list__input-container--pr">
                <RfUiDatepicker
                  required={'black'}
                  moment-locale="es"
                  position={'left'}
                  defaultMaxDateFrom={
                    timeStampJourneySelectedFinish || nowTimeStamp
                  }
                  defaultMinDateFrom={timeStampJourneySelectedStart}
                  labelInput={language.informacionPractica.dateDesdeLabel}
                  text-apply-button={language.informacionPractica.aplicarLabel}
                  onDateSelected={handleChangeStartDateSelectedService}
                  placeholder={
                    language.informacionPractica.hourInicioPlaceholderLabel
                  }
                  defaultDateFrom={calculatedStartDateSelectedService}
                />
              </div>
            </div>
            <div className="rf-box__child">
              <div className="rf-practice-create__input-container">
                <RfUiDatepicker
                  required={'black'}
                  moment-locale="es"
                  position={'left'}
                  defaultMaxDateFrom={
                    timeStampJourneySelectedFinish || nowTimeStamp
                  }
                  labelInput={language.informacionPractica.dateHastaLabel}
                  text-apply-button={language.informacionPractica.aplicarLabel}
                  onDateSelected={handleChangeFinishDateSelectedService}
                  placeholder={
                    language.informacionPractica.hourFinPlaceholderLabel
                  }
                  defaultDateFrom={calculatedFinishDateSelectedService}
                  defaultMinDateFrom={timeStampJourneySelectedStart}
                />
              </div>
            </div>
            <div className="rf-box__child">
              <div className="rf-practice-create__input-container rf-practice-list__input-container--pr">
                <RfUiInput
                  required={'black'}
                  label={language.informacionPractica.hourInicioLabel}
                  error=""
                  typeInput="time"
                  className="rf-input"
                  onValueChanges={handleChangeStartTimeSelectedService}
                  placeholder="XX:XX"
                  textError={startServiceSelectedErrorDraft}
                  value={calculatedStartTimeSelectedService}
                />
              </div>
            </div>
            <div className="rf-box__child">
              <div className="rf-practice-create__input-container">
                <RfUiInput
                  required={'black'}
                  label={language.informacionPractica.hourFinLabel}
                  error=""
                  typeInput="time"
                  className="rf-input"
                  onValueChanges={handleChangeFinishTimeSelectedService}
                  placeholder="XX:XX"
                  textError={finishTimeServiceSelectedErrorDraft}
                  value={calculatedFinishTimeSelectedService}
                />
              </div>
            </div>
            <MinutesContainer />
          </div>
        </div>
      )}
    </>
  );
};

export default ServicesContainer;
